/**
 * tanstack query用に特定のAPIエンドポイントに応じた関数群を返すhooks
 * APIに応じてstaleTimeも設定する
 * */

import { useApi } from '@/components/Functional/hooks-core/useApi'
import { useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import type {
  CampaignGetResponse,
  MenuListResponse,
  NotificationGetResponse,
  NotificationListResponse,
  NotificationsUnreadResponse,
  QuestionnaireGetResponse,
  QuestionnaireListResponse,
  RewardGetResponse,
  RewardListResponse,
  SheetListResponse,
  SpotGetResponse,
  SpotListResponse,
  StampGetResponse,
  StampQuizListResponse,
  UserGetResponse,
  UserRewardStatusGetResponse,
  UserRewardStatusListResponse,
  UserSheetListResponse,
  UserStampListResponse,
} from '@/schemas/v2'

interface FetchFn<T> {
  queryKey: string[]
  staleTime: number
  queryFn: () => Promise<T | undefined>
}

/* キャッシュをするAPIに付与するデフォルトのキャッシュ時間。1日に設定している */
const ONE_DAY_CACHE_TIME = 24 * 60 * 60 * 1000

/**
 * バックエンド master_update_timestamps のテーブル名 → TanStack Query の queryKey[0] マッピング。
 * GET /api/v2/master/update/check で true が返ったテーブルに対応するクエリを invalidate する。
 */
export const MASTER_TABLE_TO_QUERY_KEYS: Record<string, string[]> = {
  campaigns: ['campaign'],
  sheets: ['sheets'],
  menus: ['menus'],
  spots: ['spots'],
  notifications: ['notifications'],
  stamps: ['stamps', 'stamp-detail', 'sheets', 'userSheets'],
  rewards: ['rewards'],
  reward_groups: ['rewards'],
}

export const useTanstackCampaignFetchFn = (): FetchFn<CampaignGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { guest } = useApi()
  return {
    queryKey: ['campaign', campaignSlug],
    staleTime: ONE_DAY_CACHE_TIME,
    queryFn: () => guest.fetchCampaign(),
  }
}

export const useTanstackSheetsFetchFn = (): FetchFn<SheetListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { guest } = useApi()
  return {
    queryKey: ['sheets', campaignSlug],
    staleTime: ONE_DAY_CACHE_TIME,
    queryFn: () => guest.fetchSheets(),
  }
}

/**
 * 認証必須
 * staleTime: 0 — スタンプ獲得後に invalidateQueries(['userSheets']) を呼ぶことで即時更新する。
 */
export const useTanstackUserSheetsFetchFn = (): FetchFn<UserSheetListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { auth } = useApi()
  return {
    queryKey: ['userSheets', campaignSlug],
    staleTime: 0,
    queryFn: () => auth.fetchUserSheets(),
  }
}

export const useTanstackSpotsFetchFn = (): FetchFn<SpotListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { guest } = useApi()
  return {
    queryKey: ['spots', campaignSlug],
    staleTime: ONE_DAY_CACHE_TIME,
    queryFn: () => guest.fetchSpots(),
  }
}

export const useTanstackSpotFetchFn = (spotId: number): FetchFn<SpotGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { guest } = useApi()
  return {
    queryKey: ['spots', campaignSlug, String(spotId)],
    staleTime: ONE_DAY_CACHE_TIME,
    queryFn: () => guest.fetchSpot(spotId),
  }
}

/**
 * GPS座標付きスポット取得APIのファクトリフック。
 * GPS座標は動的に決まるため、useStampQuizFetchFnFactory と同じ factory パターンを採用。
 * 返された factory を `queryClient.fetchQuery(factory(spotId, lat, lon))` で使用する。
 */
export const useTanstackSpotWithLatLonFetchFnFactory = (): ((
  spotId: number,
  latitude: number,
  longitude: number,
) => FetchFn<SpotGetResponse>) => {
  const { campaignSlug } = useCampaignSlug()
  const { guest } = useApi()
  return (spotId, latitude, longitude) => ({
    queryKey: ['spots', campaignSlug, String(spotId), String(latitude), String(longitude)],
    // 60秒に深い意味はないが、短い期間に同じところから複数回実行された場合に同じ値を返すようにするため
    staleTime: 60 * 1000,
    queryFn: () => guest.fetchSpotWithLocation(spotId, latitude, longitude),
  })
}

export const useTanstackRewardsFetchFn = (): FetchFn<RewardListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { guest } = useApi()
  return {
    queryKey: ['rewards', campaignSlug],
    staleTime: ONE_DAY_CACHE_TIME,
    queryFn: () => guest.fetchRewards(),
  }
}

export const useTanstackRewardDetailFetchFn = (id: number): FetchFn<RewardGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { guest } = useApi()
  return {
    queryKey: ['rewards', campaignSlug, String(id)],
    staleTime: ONE_DAY_CACHE_TIME,
    queryFn: () => guest.fetchReward(id),
  }
}

export const useTanstackStampDetailFetchFn = (stampId: number): FetchFn<StampGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { guest } = useApi()
  return {
    queryKey: ['stamp-detail', campaignSlug, String(stampId)],
    staleTime: ONE_DAY_CACHE_TIME,
    queryFn: () => guest.fetchStamp(stampId),
  }
}

/**
 * 認証必須
 * staleTime: 0 — スタンプ獲得などで状況が変わる可能性があるため即時更新する。
 */
export const useTanstackUserRewardsFetchFn = (): FetchFn<UserRewardStatusListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { auth } = useApi()
  return {
    queryKey: ['userRewards', campaignSlug],
    staleTime: 0,
    queryFn: () => auth.fetchUserRewards(),
  }
}

/**
 * 認証必須
 * staleTime: 0 — 特典詳細の獲得状況は動的なため即時更新する。
 */
export const useTanstackUserRewardDetailFetchFn = (id: number): FetchFn<UserRewardStatusGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { auth } = useApi()
  return {
    queryKey: ['userRewards', campaignSlug, String(id)],
    staleTime: 0,
    queryFn: () => auth.fetchUserReward(id),
  }
}

export const useTanstackMenuFetchFn = (): FetchFn<MenuListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { guest } = useApi()
  return {
    queryKey: ['menus', campaignSlug],
    staleTime: ONE_DAY_CACHE_TIME,
    queryFn: () => guest.fetchMenus(),
  }
}

/**
 * 認証済みユーザーのプロフィール取得（GET /api/v2/user）
 * 認証必須。enabled: isAuthenticated を付けて使用すること。
 * staleTime: 5分 — ユーザー情報は頻繁に変わらないが、完全にキャッシュし続けるのは避ける。
 */
export const useTanstackUserProfileFetchFn = (): FetchFn<UserGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { auth } = useApi()
  return {
    queryKey: ['userProfile', campaignSlug],
    staleTime: 5 * 60 * 1000, // 5分
    queryFn: () => auth.fetchUserProfile(),
  }
}

export const useTanstackNewsFetchFn = (): FetchFn<NotificationListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { guest } = useApi()
  return {
    queryKey: ['notifications', campaignSlug],
    staleTime: 5 * 60 * 1000, // 5分キャッシュ
    queryFn: () => guest.fetchNotifications(),
  }
}

export const useTanstackNewsDetailFetchFn = (
  id: number,
  isAuthenticated: boolean,
): FetchFn<NotificationGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { guest, auth } = useApi()
  return {
    queryKey: ['notifications', campaignSlug, String(id), isAuthenticated ? 'auth' : 'guest'],
    staleTime: 5 * 60 * 1000, // 5分キャッシュ
    // ログイン済みの場合は Authorizationヘッダ付きで取得し、サーバー側の既読処理を発火させる
    queryFn: isAuthenticated ? () => auth.fetchNotification(id) : () => guest.fetchNotification(id),
  }
}

/**
 * 認証必須
 * staleTime: 0 — 未読状態はお知らせ閲覧や通知受信で変化するため常に最新を取得する。
 */
export const useTanstackNotificationsUnreadFetchFn = (): FetchFn<NotificationsUnreadResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { auth } = useApi()
  return {
    queryKey: ['notificationsUnread', campaignSlug],
    staleTime: 0,
    queryFn: () => auth.fetchNotificationsUnread(),
  }
}

/**
 * アンケート一覧取得（GET /api/v2/questionnaires）
 * 認証不要
 * staleTime: 0 — answered（回答済みフラグ）はユーザー操作で変化するため、
 * 常に最新データを取得する。POST 後に invalidateQueries(['questionnaires']) を
 * 呼び出すことでも対応可能。
 */
export const useTanstackQuestionnairesFetchFn = (): FetchFn<QuestionnaireListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { guest } = useApi()
  return {
    queryKey: ['questionnaires', campaignSlug],
    staleTime: 0,
    queryFn: () => guest.fetchQuestionnaires(),
  }
}

/**
 * タイプ指定アンケート取得（GET /api/v2/questionnaires/type/{type}）
 * 認証必須
 * staleTime: 0 — answered（回答済みフラグ）はユーザー操作で変化するため、
 * 常に最新データを取得する。POST 後に invalidateQueries(['questionnaires', 'type']) を
 * 呼び出すことでも対応可能。
 */
export const useTanstackQuestionnairesTypeFetchFn = (
  type: 'register' | 'reward',
): FetchFn<QuestionnaireGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { auth } = useApi()
  return {
    queryKey: ['questionnaires', 'type', type, campaignSlug],
    staleTime: 0,
    queryFn: () => auth.fetchQuestionnairesType(type),
  }
}

/**
 * 認証不要
 * staleTime: 0 — answered（回答済みフラグ）はユーザー操作で変化するため、
 * 常に最新データを取得する。POST 後に invalidateQueries(['questionnaires']) を
 * 呼び出すことでも対応可能。
 */
export const useTanstackQuestionnaireFetchFn = (id: number): FetchFn<QuestionnaireGetResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { guest } = useApi()
  return {
    queryKey: ['questionnaires', campaignSlug, String(id)],
    staleTime: 0,
    queryFn: () => guest.fetchQuestionnaire(id),
  }
}

/**
 * クイズ取得ファクトリー（GET /api/v2/stamps/{stampId}/quizzes）
 *
 * stampId はスポット取得後に確定するため、通常の `useTanstackXxxFetchFn(id)` パターンは使えない。
 * 代わりに「(stampId: number) => FetchFn<StampQuizListResponse>」を返す factory 関数を提供する。
 * 返された factory を Actor の input に渡し、
 * `queryClient.fetchQuery(factory(stampId))` で使用する。
 */
export const useStampQuizFetchFnFactory = (): ((stampId: number) => FetchFn<StampQuizListResponse>) => {
  const { campaignSlug } = useCampaignSlug()
  const { auth } = useApi()
  return (stampId: number): FetchFn<StampQuizListResponse> => ({
    queryKey: ['stamp-quiz', campaignSlug, String(stampId)],
    staleTime: ONE_DAY_CACHE_TIME,
    queryFn: () => auth.fetchStampQuizzes(stampId),
  })
}

/**
 * 認証必須
 * staleTime: 1時間 — 更新頻度が低いため & スタンプ獲得時には invalidateQueries(['userStamps']) を
 * 呼ぶことで即時更新されるため、長めのキャッシュ時間を設定する。
 */
export const useTanstackUserStampsFetchFn = (): FetchFn<UserStampListResponse> => {
  const { campaignSlug } = useCampaignSlug()
  const { auth } = useApi()
  return {
    queryKey: ['userStamps', campaignSlug],
    staleTime: 60 * 60 * 1000, // 1時間
    queryFn: () => auth.fetchUserStamps(),
  }
}
